import { Component, Prop, Event, EventEmitter, Host, h, State, Method, Element, Watch, Listen } from '@stencil/core';
import { metrics, getGUID } from '../../utils';
import { DisplayInputs, Typography } from '../../utils/types/index';
import { Asterisk } from '../../utils/fragments/index';

/**
 * @slot icon-leading - Used to add an `prism-icon` component into the left side of an input
 * @slot icon-trailing - Used to add an `prism-icon` component into the right side of an input
 * @cssprop  --icon-offset-x - Icon Offset X
 * @cssprop  --icon-offset-y - Icon Offset Y
 * @cssprop  --input-margin-top - Space above
 * @cssprop  --input-margin-bottom - Space below
 * @cssprop  --text-align - alignment
 * @cssprop  --input-padding-contained -  Padding of Input
 * @cssprop  --input-padding-minimal - Padding of Input Minimal Variant
 */
@Component({
  tag: 'prism-input-text',
  styleUrl: 'prism-input-text.css',
  scoped: true,
})
export class InputText {
  private uid = getGUID();
  private formatCurrentPosition: number = 0;
  private formatNextPosition: number = 0;
  private keydownData: {
    value: string;
    action: string;
    isBackspace: boolean;
  };
  /** @internal */
  @Element() el!: HTMLPrismInputTextElement;
  /** @internal Reference to inner Input */
  nativeInput!: HTMLInputElement;
  /**
   * Prism ID - Add a unique ID
   */
  @Prop() prismId: string = `prism-input-text-${this.uid}`;
  /** Friendly Name for submission */
  @Prop() name: string = this.uid;
  /** Description for Input, if none supplied, Name is used. */
  @Prop() label: string = this.name.charAt(0).toUpperCase() + this.name.replace(/-/g, ' ').substring(1);
  /** Disable Input */
  @Prop() disabled: boolean = false;
  /** Hide The Label but content still available for screen readers */
  @Prop() hideLabel: boolean = false;
  /** Hide the error icon and message but content still available for screen readers */
  @Prop() hideInvalid: boolean = false;
  /** Set Invalid on Render */
  @Prop({ mutable: true }) invalid: boolean = false;
  /** Invalid Message for this field */
  @Prop() invalidMessage = 'This field is required';
  /** Additional text for a11y */
  @Prop() ariaDescribedby: string = '';
  /** Aria Active Descendant */
  @Prop() ariaActivedescendant?: string;
  /** Aria Controls */
  @Prop() ariaControls?: string;
  /** Aria Expanded */
  // Fixme: Prism 8 - change all aria to a11y for better compatibility
  @Prop() ariaExpanded: any;
  /** Aria AutoComplete */
  @Prop() ariaAutocomplete?: string;
  /** Role */
  @Prop() role: string;
  /** State: Combined user input + error state */
  @State() describedBy!: string;
  /** Enable Auto Capitalize */
  @Prop() autoCapitalize: 'on' | 'off' = 'off';
  /** Enable Auto Complete */
  @Prop() autocomplete: 'on' | 'off' | string = 'off';
  /** Enable Auto Correct */
  @Prop() autocorrect: 'on' | 'off' = 'off';
  /** Clear on Edit */
  @Prop() clearOnEdit?: boolean;
  /** Auto Focus */
  @Prop() autoFocus: boolean = false;
  /** Hint Text, an optional description for the label */
  @Prop() hint?: string;
  /** Min Value */
  @Prop() min?: any;
  /** Max Value */
  @Prop() max?: any;
  /** Min Length */
  @Prop() minlength?: number;
  /** Max Length */
  @Prop() maxlength?: number;
  /** Pattern to Validate Against */
  @Prop() pattern?: string;
  /** Placeholder Text */
  @Prop() placeholder?: string;
  /** Required for Validation */
  @Prop() required: boolean = false;
  /** Readonly */
  @Prop() readonly?: boolean = false;
  /** Loading State */
  // @Prop() loading?: boolean = false;
  /** Input Type */
  @Prop() type: 'text' | 'number' | 'date' | 'datetime-local' | 'email' | 'month' | 'password' | 'search' | 'tel' | 'time' | 'url' | 'week' = 'text';
  /** Value */
  @Prop() value?: string | number | null = '';
  /** Enable obfuscation of input between password and text */
  @Prop() obfuscate = false;
  /** Obfuscate Button Label */
  @Prop() obfuscateLabel = 'toggle password visibility';
  /** Variant **/
  @Prop() variant: DisplayInputs = 'contained';
  /** Text Display */
  @Prop() textDisplay: Extract<Typography, 'body1' | 'heading1'> = 'body1';
  /** Input Mode */
  @Prop() inputmode: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url' = 'text';
  /** Format the input as its typed. Supports only numbers. Ex. ##/##/#### */
  @Prop() format?: string;
  private isComposing = false;
  /** @internal */
  @State() hintId = `${this.prismId}-hint`;
  // Dirty Check - Testing for user interaction for validation
  private isDirty: boolean = false;
  /** Value Change Event */
  @Event() prismChange!: EventEmitter;
  /**
   * Emitted when the input has changed.
   */
  @Event() prismInput!: EventEmitter;
  /**
   * Emitted on keyup
   */
  @Event() prismKeyup!: EventEmitter;
  /**
   * Emitted when the input loses focus.
   */
  @Event() prismBlur!: EventEmitter;
  /**
   * Emitted when the input has focus.
   */
  @Event() prismFocus!: EventEmitter;
  /** Emitted on Invalid  */
  @Event({ bubbles: true }) prismInvalid!: EventEmitter;
  /* Listen for Pasted Value from Clipboard */
  @Listen('paste')
  getClipboardData(event: ClipboardEvent) {
    let copiedText = event.clipboardData.getData('text');
    // TODO: Rename Event for clipboard
    this.prismChange.emit({ name: 'value-changed', value: copiedText });
  }
  /** @internal */
  @State() valid: boolean = false;
  /** @internal */
  @State() elementFocused: boolean = false;
  /** @internal */
  @State() ariaMessage: string = '';
  /**
   * @description Public: Set Focus on Input
   */
  @Method()
  async setFocus() {
    if (this.nativeInput) {
      this.nativeInput.focus();
    }
  }
  /** @internal */
  slots = {
    'icon-leading': false,
    'icon-trailing': false,
  };
  private onCompositionStart = () => {
    this.isComposing = true;
  };

  private onCompositionEnd = () => {
    this.isComposing = false;
  };
  private getValue(): string {
    let value = typeof this.value === 'number' ? this.value.toString() : (this.value || '').toString();

    if (this.format && value) {
      const [_, formattedValue] = this.formatValidCharacters(value);
      value = formattedValue;
    }

    return value;
  }
  /**
   * Update the native input element when the value changes
   */
  @Watch('value')
  protected valueWatchChanged() {
    // This check is added to watch for values not entered with keyboard
    if (this.format) {
      this.isDirty = true;
    }
    const nativeInput = this.nativeInput;
    const value = this.getValue();
    if (nativeInput && nativeInput.value !== value && !this.isComposing) {
      /**
       * Assigning the native input's value on attribute
       * value change, allows `ionInput` implementations
       * to override the control's value.
       *
       * Used for patterns such as input trimming (removing whitespace),
       * or input masking.
       */
      nativeInput.value = value;
    }
    this.prismChange.emit({ id: this.prismId, value: this.value == null ? this.value : this.value.toString() });
  }
  // Emit onChange of Value Event
  /** @internal */
  valueChanged(event: any) {
    this.el?.setAttribute('value', event.target.value);
    this.prismChange.emit({ name: 'value-changed', value: event.target.value });
  }
  // Validity Check
  /** @internal */
  onCheckValidity(event: any) {
    // disables the browser popup
    event.preventDefault();
    let ct = event.currentTarget;
    this.invalid = !ct.validity.valid;
    this.valid = ct.validity.valid;
    this.describedBy = `${this.hintId} ${this.ariaDescribedby}`.trim();
    this.prismInvalid.emit({ name: 'check-validity', value: ct.value, target: ct, reason: ct.validity });
    this.prismInput.emit({ name: 'check-validity', value: ct.value, target: ct, reason: ct.validity });
  }
  /**
   * Set custom validity error when not every formatted template is entered
   */
  checkFormatValidity(element: HTMLInputElement, value: string) {
    // format requires all or none of the input to be given
    // ex... '' => valid  '(123)' => invalid  '(123) 456-7890' => valid
    element.setCustomValidity(value.length > 0 && this.format.length !== value.length ? 'formatting' : '');
  }
  /** @internal */
  inputChanged(event: any) {
    this.isDirty = true;
    this.describedBy = `${this.hintId} ${this.ariaDescribedby}`.trim();

    if (this.format) {
      this.inputChangedWithFormatting(event);
    } else {
      let ct = event.currentTarget;
      this.prismInput.emit({ name: 'input-change', value: ct.value, target: ct });
    }
  }
  /** @internal */
  inputChangedWithFormatting(event: CustomEvent): boolean {
    const ct = event.currentTarget as HTMLInputElement;
    const userInput = ct.value;

    const [safeValue, formattedSafeValue] = this.formatValidCharacters(userInput);

    // if the next position was not decided, do that now
    if (this.formatNextPosition === -1) {
      this.formatNextPosition = this.findNextPosition(this.formatCurrentPosition);
    }

    this.nativeInput.value = formattedSafeValue;

    // the value never changed... likely because they entered an invalid character
    if (!this.keydownData.isBackspace && formattedSafeValue === this.keydownData.value) {
      this.nativeInput.setSelectionRange(this.formatCurrentPosition, this.formatCurrentPosition);
      // announce to screen reader
      this.setAriaMessage('Invalid character');
      return false;
    }

    // move cursor only when formatting has changed
    this.nativeInput.setSelectionRange(this.formatNextPosition, this.formatNextPosition);

    this.prismInput.emit({
      name: 'input-change-format',
      value: safeValue,
      formattedValue: formattedSafeValue,
      target: ct,
      dirty: this.isDirty,
      invalid: this.invalid,
    });
  }
  /** @internal */
  onKeyup(event: KeyboardEvent) {
    let ct = event.currentTarget as HTMLInputElement;
    this.prismKeyup.emit({
      name: 'input-keyup',
      type: event.type,
      key: event.key,
      code: event.code,
      target: event.target,
      valid: ct.validity.valid,
    });
  }
  /** @internal */
  onKeydown(event: KeyboardEvent) {
    if (!this.format) {
      return;
    }
    const target = event.target as HTMLInputElement;
    // save value to compare with during onInput
    this.keydownData = {
      value: target.value,
      action: event.key,
      isBackspace: event.key === 'Backspace',
    };

    // enabled when you are selecting more than one character
    const selectionMode: boolean = target.selectionStart !== target.selectionEnd;
    this.formatCurrentPosition = target.selectionStart;
    this.formatNextPosition = -1; // always reset it

    if (selectionMode && (event.key === 'Backspace' || event.key === 'Delete')) {
      this.formatNextPosition = this.formatCurrentPosition;
    } else if (event.key === 'Backspace') {
      // backspace at the first index doesn't do anything
      if (this.formatCurrentPosition === 0) {
        event.preventDefault();
        return;
      }

      this.formatNextPosition = this.formatCurrentPosition - 1;
    } else if (event.key === 'Delete') {
      this.formatNextPosition = this.findNextPosition(this.formatCurrentPosition, true);

      // if they're interacting with a 'reserved format' position
      if (this.format[this.formatCurrentPosition] !== '#') {
        target.setSelectionRange(this.formatNextPosition, this.formatNextPosition);
        event.preventDefault();
      }
    }
  }
  // Input has focus or blurred
  /** @internal */
  inputState(event: any) {
    let ct = event.currentTarget;
    switch (event.type) {
      case 'blur':
        // special validity checks for formatted values
        if (this.format) {
          this.checkFormatValidity(ct, ct.value);
        }
        // Re-Check Validity on Change of the Value
        if (this.isDirty) {
          this.invalid = !ct.validity.valid;
          this.valid = ct.validity.valid;
        }
        this.elementFocused = false;
        this.prismBlur.emit({
          name: 'input-blurred',
          value: ct.value,
          target: ct,
          valid: this.valid,
          dirty: this.isDirty,
          reason: ct.validity,
        });
        break;
      case 'focus':
        this.elementFocused = true;
        this.prismFocus.emit({ name: 'input-focused', value: ct.value, target: ct });
    }
  }
  connectedCallback() {
    this.describedBy = this.hint ? `${this.hintId} ${this.ariaDescribedby}`.trim() : this.ariaDescribedby;
    // Add Leading Icon Class
    if (!!this.el.querySelector('[slot="icon-leading"]')) this.el.querySelector('[slot="icon-leading"]').setAttribute('css-slot', 'icon-leading');
    // Add Trailing Icon Class
    if (!!this.el.querySelector('[slot="icon-trailing"]')) this.el.querySelector('[slot="icon-trailing"]').setAttribute('css-slot', 'icon-trailing');
    this.slots['icon-leading'] = !!this.el.querySelector('[slot="icon-leading"]');
    this.slots['icon-trailing'] = !!this.el.querySelector('[slot="icon-trailing"]');

    // Component Data Layer
    metrics('prism-input-text');
  }
  componentDidLoad() {
    const nativeInput = this.nativeInput;
    if (nativeInput) {
      // TODO: FW-729 Update to JSX bindings when Stencil resolves bug with:
      // https://github.com/ionic-team/stencil/issues/3235
      nativeInput.addEventListener('compositionstart', this.onCompositionStart);
      nativeInput.addEventListener('compositionend', this.onCompositionEnd);
      if (this.format) {
        this.checkFormatValidity(this.nativeInput, this.nativeInput?.value);
      }
    }
  }
  // Toggle the show/hide of the content in the input
  /** @internal */
  _revealToggle() {
    this.type = this.type === 'password' ? 'text' : 'password';
  }
  /** @internal */
  formatValidCharacters(inputValue: string = ''): [string, string] {
    if (!inputValue) {
      return ['', ''];
    }

    let rawValue: string = '';
    let formattedValue: string = '';

    // check input matches format
    let inputIndex: number = 0;
    let formatIndex: number = 0;

    while (inputIndex < inputValue.length && formatIndex < this.format.length) {
      if (this.format[formatIndex] === '#') {
        const valid = /\d/.test(inputValue[inputIndex]);
        if (valid) {
          formattedValue += inputValue[inputIndex];
          rawValue += inputValue[inputIndex];

          inputIndex++;
          formatIndex++;
        } else {
          inputIndex++;
        }
      } else {
        if (this.format[formatIndex] === inputValue[inputIndex]) {
          inputIndex++;
        }

        formattedValue += this.format[formatIndex];
        formatIndex++;
      }
    }

    return [rawValue, formattedValue];
  }
  findNextPosition(start: number, isDelete: boolean = false) {
    let nextPosition: number = start;

    // continue to find the index that doesn't match the placeholder character
    while (nextPosition < this.format.length) {
      if (this.format[nextPosition] === '#') {
        break;
      }

      nextPosition++;
    }

    // On delete, we want to keep the cursor in the same spot
    // Otherwise, we want to move the cursor right after the index is found
    return isDelete ? nextPosition : nextPosition + 1;
  }
  findPreviousPosition(start: number): number {
    if (start === 0) {
      return start;
    }

    let nextPosition: number = start - 1;

    // if the character behind the cursor is a template, stop immediately
    if (this.format[nextPosition] === '#') {
      return nextPosition;
    }

    while (nextPosition > 0) {
      // we need to look behind the cursor to prepare for the next typed character
      if (this.format[nextPosition - 1] === '#') {
        nextPosition;
        break;
      }

      nextPosition--;
    }

    return nextPosition;
  }
  setAriaMessage(message: string) {
    this.ariaMessage = message;

    // reset message so next message will get read, even if it's the same message
    setTimeout(() => {
      this.ariaMessage = '';
    }, 10);
  }
  render() {
    const displayHintRegion = this.invalid || this.hint;
    const value = this.getValue();
    return (
      <Host class={{ [this.variant]: true }}>
        <span class="screen-reader-text" aria-live="assertive">
          {this.ariaMessage}
        </span>
        <label
          class={{
            invalid: this.invalid,
          }}
          htmlFor={this.prismId}
        >
          <prism-text
            tag="span"
            display="caption"
            class={{
              'invalid': this.invalid,
              'label-text': true,
              'screen-reader-text': this.hideLabel,
            }}
          >
            {this.label}
            {this.required && !this.hideLabel && <Asterisk />}
          </prism-text>
        </label>
        <span
          class={{
            wrap: true,
            invalid: this.invalid,
          }}
        >
          {this.type === 'search' ? <prism-icon class={{ ['icon-search']: true }} name="search"></prism-icon> : <slot name="icon-leading"></slot>}
          <input
            class={{
              'input': true,
              'disabled': this.disabled,
              'invalid': this.invalid,
              'valid': this.valid,
              'icon-leading': this.slots['icon-leading'] || this.type === 'search',
              'icon-trailing': this.slots['icon-trailing'] || this.obfuscate,
              [this.type]: true,
              [this.variant]: true,
              [this.textDisplay]: true,
            }}
            ref={input => (this.nativeInput = input as HTMLInputElement)}
            id={this.prismId}
            disabled={this.disabled}
            autocapitalize={this.autoCapitalize}
            autocomplete={this.autocomplete}
            autocorrect={this.autocorrect}
            autoFocus={this.autoFocus}
            inputmode={this.inputmode}
            min={this.min}
            max={this.max}
            minlength={this.minlength}
            maxlength={this.maxlength}
            name={this.name}
            pattern={this.pattern}
            placeholder={this.placeholder}
            required={this.required}
            role={this.role}
            readonly={this.readonly}
            type={this.type}
            value={value}
            aria-invalid={this.invalid.toString()}
            aria-required={this.required.toString()}
            aria-describedby={Boolean(this.describedBy) ? this.describedBy : false}
            aria-activedescendant={this.ariaActivedescendant}
            aria-controls={this.ariaControls}
            aria-expanded={this.ariaExpanded?.toString()}
            aria-autocomplete={this.ariaAutocomplete}
            onInput={e => this.inputChanged(e)}
            onInvalid={e => this.onCheckValidity(e)}
            onChange={e => this.valueChanged(e)}
            onBlur={e => this.inputState(e)}
            onFocus={e => this.inputState(e)}
            onKeyDown={e => this.onKeydown(e)}
            onKeyUp={e => this.onKeyup(e)}
          />
          {/* Slot for Trailing Icons */}

          {this.obfuscate ? (
            <prism-button size="xs" class={'icon-trailing-manual'} label={this.obfuscateLabel} onPrismPress={() => this._revealToggle()} display="ghost-icon">
              <prism-icon name={this.type === 'password' ? 'eye' : 'eye_hide'} />
            </prism-button>
          ) : (
            <slot name="icon-trailing"></slot>
          )}
          {/* <prism-loading type="spinner"></prism-loading> */}
        </span>
        {displayHintRegion && (
          <span
            class={{
              'hint-container': true,
              'screen-reader-text': this.invalid && this.hideInvalid,
            }}
          >
            {this.invalid && <prism-icon theme="fill" slot="icon-trailing" size="xs" tone="negative" name="alert" />}
            <prism-text
              class={{
                ['invalid-text']: this.invalid,
                'hint-text': true,
              }}
              display="caption"
              id={this.hintId}
            >
              {this.invalid ? this.invalidMessage : this.hint}
            </prism-text>
          </span>
        )}
      </Host>
    );
  }
}
